import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { type CustomTabId } from './customTabIds';
import { type PageTab, type TabKey } from './tabKey';
import { logout } from '../session/logoutThunk';
import type { RootState } from '../store';

type TabsState = Readonly<{
  openCustomTabs: CustomTabId[];
  selectedTabKey: TabKey | null;
  pageTabs: PageTab[];
}>;

const initialState: TabsState = {
  openCustomTabs: [],
  selectedTabKey: null,
  pageTabs: [],
};

export const tabsSlice = createSlice({
  name: 'tabs',
  initialState,
  reducers: {
    openCustomTab: (state, action: PayloadAction<CustomTabId>) => {
      const id = action.payload;

      if (!state.openCustomTabs.includes(id)) {
        state.openCustomTabs.push(id);
      }

      state.selectedTabKey = `custom:${id}`;
    },
    closeCustomTab: (state, action: PayloadAction<CustomTabId>) => {
      state.openCustomTabs = state.openCustomTabs.filter((id) => id !== action.payload);
    },
    setSelectedTabKey: (state, action: PayloadAction<TabKey | null>) => {
      state.selectedTabKey = action.payload;
    },
    setPageTabs: (state, action: PayloadAction<readonly PageTab[]>) => {
      state.pageTabs = [...action.payload];
    },
    clearPageTabs: (state) => {
      state.pageTabs = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState);
  },
});

export const { openCustomTab, closeCustomTab, setSelectedTabKey, setPageTabs, clearPageTabs } =
  tabsSlice.actions;

export const selectPageTabs = (state: RootState): readonly PageTab[] => state.tabs.pageTabs;

export const selectOpenCustomTabs = (state: RootState): readonly CustomTabId[] =>
  state.tabs.openCustomTabs;

export const selectSelectedTabKey = (state: RootState): TabKey | null => state.tabs.selectedTabKey;
